/**
 * UserTagSection - Display and manage user's @tag
 * Includes claiming, display, sharing with QR code
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { 
  AtSign, 
  Check, 
  X, 
  Loader2, 
  Copy, 
  Share2, 
  QrCode, 
  ExternalLink,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import api from '../lib/api';
import { debounce } from 'lodash';

const UserTagSection = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [myTag, setMyTag] = useState(null);
  const [claimInput, setClaimInput] = useState('');
  const [checking, setChecking] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showReleaseDialog, setShowReleaseDialog] = useState(false);
  const [releasing, setReleasing] = useState(false);

  // Fetch user's current tag
  const fetchMyTag = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/v1/tags/me');
      setMyTag(data.has_tag ? data : null);
    } catch (err) {
      console.error('Failed to fetch tag:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyTag();
  }, []);

  // Debounced availability check
  const checkAvailability = useCallback(
    debounce(async (tag) => {
      if (!tag || tag.length < 3) {
        setAvailability(null);
        return;
      }

      setChecking(true);
      try {
        const { data } = await api.get(`/v1/tags/check/${tag}`);
        setAvailability(data);
      } catch (err) {
        if (err.response?.status === 429) {
          setAvailability({ available: false, reason: 'Too many checks. Please wait.' });
        } else {
          setAvailability({ available: false, reason: 'Error checking availability' });
        }
      } finally {
        setChecking(false);
      }
    }, 400),
    []
  );

  useEffect(() => {
    const cleanTag = claimInput.replace('@', '');
    if (cleanTag.length >= 3) {
      checkAvailability(cleanTag);
    } else {
      setAvailability(null);
    }
  }, [claimInput, checkAvailability]);

  const handleInputChange = (e) => {
    let value = e.target.value;
    // Remove @ and only keep alphanumeric + underscore
    value = value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase();
    setClaimInput(value);
  };

  const handleClaim = async () => {
    if (!claimInput || claimInput.length < 3) {
      toast.error('Tag must be at least 3 characters');
      return;
    }

    setClaiming(true);
    try {
      const { data } = await api.post('/v1/tags/claim', { tag: claimInput });
      toast.success(`Successfully claimed ${data.tag_display}!`);
      setMyTag({
        has_tag: true,
        tag: data.tag,
        tag_display: data.tag_display,
        claimed_at: data.claimed_at,
        profile_url: `https://parachpay.com/u/${data.tag}`
      });
      setClaimInput('');
      setAvailability(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to claim tag');
    } finally {
      setClaiming(false);
    }
  };

  const handleRelease = async () => {
    setReleasing(true);
    try {
      const { data } = await api.delete('/v1/tags/me');
      toast.success(data.message);
      setMyTag(null);
      setShowReleaseDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to release tag');
    } finally {
      setReleasing(false);
    }
  };

  const copyTag = () => {
    navigator.clipboard.writeText(myTag?.tag_display || '');
    toast.success('Tag copied to clipboard!');
  };

  const copyProfileLink = () => {
    navigator.clipboard.writeText(myTag?.profile_url || '');
    toast.success('Profile link copied!');
  };

  const shareProfile = async () => {
    if (navigator.share && myTag) {
      try {
        await navigator.share({
          title: `Pay me on ParachPay`,
          text: `Send money to my ParachPay account using my tag: ${myTag.tag_display}`,
          url: myTag.profile_url
        });
      } catch (err) {
        copyProfileLink();
      }
    } else {
      copyProfileLink();
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="user-tag-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AtSign className="h-5 w-5 text-emerald-500" />
            ParachPay Tag
          </CardTitle>
          <CardDescription>
            Your unique @tag for receiving payments easily
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {myTag ? (
            // User has a tag - show it
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
                <div className="flex-1">
                  <p className="text-2xl font-bold font-mono text-emerald-600 dark:text-emerald-400">
                    {myTag.tag_display}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Claimed on {new Date(myTag.claimed_at).toLocaleDateString()}
                  </p>
                </div>
                <Badge className="bg-emerald-500 text-white">Active</Badge>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={copyTag} data-testid="copy-tag-btn">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Tag
                </Button>
                <Button variant="outline" size="sm" onClick={copyProfileLink} data-testid="copy-link-btn">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Copy Link
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowQRDialog(true)} data-testid="show-qr-btn">
                  <QrCode className="h-4 w-4 mr-2" />
                  QR Code
                </Button>
                <Button variant="outline" size="sm" onClick={shareProfile} data-testid="share-profile-btn">
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </div>

              <div className="pt-4 border-t">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setShowReleaseDialog(true)}
                  data-testid="release-tag-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Release Tag
                </Button>
                <p className="text-xs text-muted-foreground mt-1">
                  Released tags have a 30-day cooldown before anyone can claim them again
                </p>
              </div>
            </div>
          ) : (
            // No tag - show claim form
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="claim-tag">Claim your @tag</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
                    <Input
                      id="claim-tag"
                      type="text"
                      placeholder="yourname"
                      value={claimInput}
                      onChange={handleInputChange}
                      className={`pl-8 h-12 rounded-xl ${
                        availability?.available === true ? 'border-emerald-500 bg-emerald-500/5' :
                        availability?.available === false ? 'border-red-500 bg-red-500/5' : ''
                      }`}
                      maxLength={20}
                      data-testid="claim-tag-input"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {checking && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                      {!checking && availability?.available === true && (
                        <Check className="h-5 w-5 text-emerald-500" />
                      )}
                      {!checking && availability?.available === false && (
                        <X className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                  </div>
                  <Button 
                    onClick={handleClaim}
                    disabled={claiming || !availability?.available}
                    className="h-12 px-6"
                    data-testid="claim-tag-submit"
                  >
                    {claiming ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Claim'}
                  </Button>
                </div>
                {availability && !availability.available && (
                  <div className="space-y-2">
                    <p className="text-sm text-red-500">{availability.reason}</p>
                    {/* Tag Suggestions */}
                    {availability.suggestions && availability.suggestions.length > 0 && (
                      <div className="bg-muted/50 rounded-lg p-3">
                        <p className="text-xs text-muted-foreground mb-2">Try one of these available tags:</p>
                        <div className="flex flex-wrap gap-2">
                          {availability.suggestions.map((suggestion) => (
                            <Button
                              key={suggestion}
                              variant="outline"
                              size="sm"
                              className="text-xs h-7 px-2"
                              onClick={() => setClaimInput(suggestion)}
                            >
                              @{suggestion}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {availability?.available && (
                  <p className="text-sm text-emerald-500">@{claimInput} is available!</p>
                )}
              </div>

              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm text-muted-foreground">
                  <strong>Tag rules:</strong> 3-20 characters, letters, numbers, and underscores only.
                  Once claimed, share your @tag to receive money!
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Your ParachPay QR Code</DialogTitle>
            <DialogDescription>
              Share this QR code to receive payments
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="bg-white p-4 rounded-xl">
              <QRCodeSVG 
                value={myTag?.profile_url || ''} 
                size={200}
                level="H"
                includeMargin
              />
            </div>
            <p className="text-xl font-bold font-mono text-emerald-600">
              {myTag?.tag_display}
            </p>
            <p className="text-sm text-muted-foreground text-center">
              Scan to send money to this account
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={copyProfileLink}>
              <Copy className="h-4 w-4 mr-2" />
              Copy Link
            </Button>
            <Button onClick={shareProfile}>
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Release Confirmation Dialog */}
      <Dialog open={showReleaseDialog} onOpenChange={setShowReleaseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Release Tag?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to release <strong>{myTag?.tag_display}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <p className="text-sm text-amber-700 dark:text-amber-400">
                <strong>Warning:</strong> Once released, this tag will be available 
                for anyone to claim after a 30-day cooldown period. You will not 
                be able to reclaim it unless no one else does.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReleaseDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRelease}
              disabled={releasing}
            >
              {releasing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Release Tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserTagSection;
